import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import Logo from './Logo';
import Button from './ui/Button';

const Footer: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataObj = new FormData();
      formDataObj.append('form-name', 'newsletter');
      formDataObj.append('email', email);

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formDataObj as any).toString(),
      });

      if (response.ok) {
        toast.success('Successfully subscribed!');
        setEmail('');
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
      console.error('Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const navigationLinks = [
    { name: 'Home', path: '/#home' },
    { name: 'Services', path: '/#services' },
    { name: 'About', path: '/#about' },
    { name: 'Pricing', path: '/#pricing' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/#contact' }
  ];

  return (
    <footer className="bg-[#E8EFE8] text-dark pt-16 pb-8">
      <Toaster position="bottom-center" />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <div className="text-[#1b4f72]">
              <Logo className="mb-4" />
            </div>
            <p className="mt-4 text-dark/80 text-sm leading-relaxed">
              Your Property Is Our Priority.
              <br /><br />
              Tech-Forward | Transparent | Top-Notch
              <br /><br />
              
            </p>
            <div className="mt-6 flex space-x-4">
              <SocialIcon 
                icon={<Facebook size={18} />} 
                href="https://facebook.com/rentsparks"
              />
              <SocialIcon 
                icon={<Instagram size={18} />} 
                href="https://instagram.com/rentsparks"
              />
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="font-abadi text-lg font-medium mb-4">Navigation</h3>
            <ul className="space-y-2">
              {navigationLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-dark/70 hover:text-secondary transition-colors duration-200"
                    onClick={(e) => {
                      if (link.path.startsWith('/#')) {
                        e.preventDefault();
                        document.querySelector(link.path.substring(1))?.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-abadi text-lg font-medium mb-4">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start">
                <MapPin className="mr-3 h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                <span className="text-dark/70">Los Angeles, CA</span>
              </li>
              <li className="flex items-center">
                <Phone className="mr-3 h-5 w-5 text-secondary flex-shrink-0" />
                <a 
                  href="tel:+14242777216" 
                  className="text-dark/70 hover:text-secondary transition-colors duration-200"
                >
                  (424) 277-7216
                </a>
              </li>
              <li className="flex items-center">
                <Mail className="mr-3 h-5 w-5 text-secondary flex-shrink-0" />
                <a 
                  href="mailto:info@rentsparks.com"
                  className="text-dark/70 hover:text-secondary transition-colors duration-200"
                >
                  info@rentsparks.com
                </a>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="font-abadi text-lg font-medium mb-4">Newsletter</h3>
            <p className="text-dark/70 mb-4">Subscribe to our newsletter for updates and offers.</p>
            <form
              className="space-y-2"
              onSubmit={handleSubscribe}
              name="newsletter"
              method="POST"
              data-netlify="true"
              netlify-honeypot="bot-field"
            >
              <input type="hidden" name="form-name" value="newsletter" />
              <p className="hidden">
                <label>Don't fill this out: <input name="bot-field" /></label>
              </p>
              <input
                type="email"
                name="email"
                placeholder="Your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 rounded-md bg-light/80 text-dark border border-light/20 focus:outline-none focus:border-secondary"
                required
              />
              <Button
                variant="secondary"
                className="w-full"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </form>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-dark/10 text-center">
          <p className="text-dark font-medium">© {new Date().getFullYear()} RentSparks. All rights reserved.</p>
          <div className="mt-2 space-x-4 text-dark font-medium">
            <Link to="/privacy-policy" className="hover:text-secondary transition-colors duration-200">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-secondary transition-colors duration-200">Terms of Service</Link>
            <Link to="/license" className="hover:text-secondary transition-colors duration-200">License Info</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

interface SocialIconProps {
  icon: React.ReactNode;
  href: string;
}

const SocialIcon: React.FC<SocialIconProps> = ({ icon, href }) => {
  return (
    <a 
      href={href}
      target="_blank"
      rel="noopener noreferrer" 
      className="h-8 w-8 rounded-full bg-[#FFF3E0] flex items-center justify-center hover:bg-secondary transition-colors duration-200"
    >
      {icon}
    </a>
  );
};

export default Footer;